// components/portal/InventoryMetricsBar.tsx
'use client';

import React, { useMemo } from 'react';
import { useKernelStore } from '@/kernel/kernelStores';
import type { InventoryEntityType } from '#types/types';
import { HeadlineStats, type HeadlineStat } from '@/components/portal/HeadlineStats';

// A compact summary bar shown on top of every inventory table/grid: the accurate
// total item count plus a few entity-relevant metrics. Metrics that are sums over
// the currently loaded rows (not the full set) are flagged `approx` and rendered
// with a leading "~" while the list is still paginating, so nothing reads as a
// precise full-portfolio figure when only a page is loaded. Counts (the total) come
// straight from the server pagination and are always exact.
type Metric = { label: string; value: string; approx?: boolean; sub?: string };
type Lang = 'en' | 'fr';

const nf = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });
const fmt = (n: number): string => nf.format(Math.round(n));
const rec = (x: unknown): Record<string, unknown> =>
  x !== null && typeof x === 'object' ? (x as Record<string, unknown>) : {};
const asNum = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) ? v : 0);
const asStr = (v: unknown): string => (typeof v === 'string' ? v : '');
const sumBy = (items: readonly unknown[], key: string): number =>
  items.reduce((acc: number, it) => acc + asNum(rec(it)[key]), 0);
const countWhere = (items: readonly unknown[], pred: (r: Record<string, unknown>) => boolean): number =>
  items.reduce((acc: number, it) => acc + (pred(rec(it)) ? 1 : 0), 0);
const pct = (n: number, d: number): number => (d > 0 ? Math.round((n / d) * 100) : 0);

// Label dictionary (sentence case, EN/FR). No all-caps + tracking (house rule).
const L = {
  totalAssets: { en: 'Total assets', fr: 'Total actifs' },
  units: { en: 'Units', fr: 'Unites' },
  occupancy: { en: 'Occupancy', fr: 'Occupation' },
  vacant: { en: 'Vacant', fr: 'Vacant' },
  totalTenants: { en: 'Total tenants', fr: 'Total enseignes' },
  totalContacts: { en: 'Total contacts', fr: 'Total contacts' },
  withEmail: { en: 'With email', fr: 'Avec email' },
  withPhone: { en: 'With phone', fr: 'Avec telephone' },
  companies: { en: 'Companies', fr: 'Entreprises' },
  totalPortfolios: { en: 'Total portfolios', fr: 'Total portefeuilles' },
  countries: { en: 'Countries', fr: 'Pays' },
  avgAssets: { en: 'Avg assets', fr: 'Actifs moy.' },
  waitingDecision: { en: 'Waiting for a decision', fr: 'En attente de décision' },
  approvedExamples: { en: 'Approved examples', fr: 'Exemples approuvés' },
  heldOut: { en: 'Held out for measurement', fr: 'Réservés à la mesure' },
  rejected: { en: 'Rejected', fr: 'Rejetés' },
} as const;

const t = (key: keyof typeof L, lang: Lang): string => L[key][lang];

type Builder = (items: readonly unknown[], total: number, lang: Lang, aggregations?: Record<string, unknown>) => Metric[];

const BUILDERS: Partial<Record<InventoryEntityType, Builder>> = {
  SlideCorrection: (items, _total, lang, aggregations) => {
    // The route counts every row; the loaded page is the fallback while the first answer is in flight.
    const by = (decision: string, key: string) => typeof aggregations?.[key] === 'number' ? aggregations[key] as number : countWhere(items, (r) => r.decision === decision);
    return [
      { label: t('waitingDecision', lang), value: fmt(by('pending', 'pending')) },
      { label: t('approvedExamples', lang), value: fmt(by('approved', 'approved')) },
      { label: t('heldOut', lang), value: fmt(by('held-out', 'heldOut')) },
      { label: t('rejected', lang), value: fmt(by('rejected', 'rejected')) },
    ];
  },
  Property: (items, total, lang) => {
    const totalArea = sumBy(items, 'totalAreaSqm');
    const vacantArea = sumBy(items, 'vacantAreaSqm');
    // The list DTO carries the unit count as totalUnits (declaredUnitCount / unitIds
    // are older fallbacks); this is the same value the Units column renders.
    const units = items.reduce((acc: number, it) => {
      const r = rec(it);
      const tu = r.totalUnits;
      if (typeof tu === 'number' && Number.isFinite(tu)) return acc + tu;
      const du = r.declaredUnitCount;
      if (typeof du === 'number' && Number.isFinite(du)) return acc + du;
      return acc + (Array.isArray(r.unitIds) ? r.unitIds.length : 0);
    }, 0);
    let occupancy: number;
    if (totalArea > 0) {
      occupancy = pct(totalArea - vacantArea, totalArea);
    } else {
      // Fall back to the mean occupancy rate; DTOs may carry it as a 0-1 fraction
      // or a 0-100 percent, so normalize before averaging.
      const rated = items.map(rec).filter((r) => typeof r.occupancyRate === 'number');
      const meanRate =
        rated.length > 0
          ? rated.reduce((acc, r) => {
              const raw = asNum(r.occupancyRate);
              return acc + (raw <= 1 ? raw * 100 : raw);
            }, 0) / rated.length
          : 0;
      occupancy = Math.round(meanRate);
    }
    return [
      { label: t('totalAssets', lang), value: fmt(total) },
      { label: t('units', lang), value: fmt(units), approx: true },
      { label: t('occupancy', lang), value: `${occupancy}%` },
      { label: t('vacant', lang), value: `${fmt(vacantArea)} m²`, approx: true },
    ];
  },
  Brand: (_items, total, lang) => [{ label: t('totalTenants', lang), value: fmt(total) }],
  Contact: (items, total, lang) => {
    const loaded = items.length || 1;
    const withEmail = countWhere(items, (r) => asStr(r.email).trim().length > 0);
    const withPhone = countWhere(
      items,
      (r) => asStr(r.primaryPhone).trim().length > 0 || asStr(r.directPhone).trim().length > 0
    );
    const companies = new Set(
      items.map((it) => asStr(rec(it).organizationName).trim().toLowerCase()).filter((s) => s.length > 0)
    ).size;
    return [
      { label: t('totalContacts', lang), value: fmt(total) },
      { label: t('withEmail', lang), value: `${pct(withEmail, loaded)}%` },
      { label: t('withPhone', lang), value: `${pct(withPhone, loaded)}%` },
      { label: t('companies', lang), value: fmt(companies), approx: true },
    ];
  },
  Portfolio: (items, total, lang) => {
    // Match the card badge, which prefers realPropertyCount and falls back to propertyCount.
    const assets = items.reduce((acc: number, it) => {
      const r = rec(it);
      const real = r.realPropertyCount;
      return acc + (typeof real === 'number' && Number.isFinite(real) ? real : asNum(r.propertyCount));
    }, 0);
    const countries = new Set(
      items.map((it) => asStr(rec(it).country).trim().toUpperCase()).filter((s) => s.length > 0)
    ).size;
    const avg = items.length > 0 ? assets / items.length : 0;
    return [
      { label: t('totalPortfolios', lang), value: fmt(total) },
      { label: t('totalAssets', lang), value: fmt(assets), approx: true },
      { label: t('countries', lang), value: fmt(countries), approx: true },
      { label: t('avgAssets', lang), value: fmt(avg) },
    ];
  },
};

interface InventoryMetricsBarProps {
  entityType: InventoryEntityType;
  items: readonly unknown[];
  total?: number | null;
  aggregations?: Record<string, unknown> | null;
  /** Where the figures sit; by default on the page title's 24px edge. */
  className?: string;
}

export const InventoryMetricsBar = React.memo(({ entityType, items, total, aggregations, className }: InventoryMetricsBarProps) => {
  const lang = (useKernelStore((s) => s.language) as Lang) ?? 'en';
  const resolvedTotal = typeof total === 'number' && total >= items.length ? total : items.length;
  const isPartial = resolvedTotal > items.length;

  const metrics = useMemo<Metric[]>(() => {
    const builder = BUILDERS[entityType];
    return builder ? builder(items, resolvedTotal, lang, aggregations ?? undefined) : [];
  }, [entityType, items, resolvedTotal, lang, aggregations]);

  // Only inventory entities with a defined metric builder (Property / Brand /
  // Contact / Portfolio) get a bar; everything else (e.g. the Studio Projects
  // grid) renders nothing.
  if (metrics.length === 0) return null;
  if (items.length === 0 && resolvedTotal === 0) return null;

  // Render with the shared HeadlineStats treatment (mono figure over label, hairline
  // dividers), the same component the entity headers use. The "~" approx marker is
  // baked into the value for sums that are still over a partial page.
  const headlineStats: HeadlineStat[] = metrics.map((m) => ({
    value: m.approx && isPartial ? `~${m.value}` : m.value,
    label: m.label,
    sub: m.sub,
  }));
  return (
    <div className={className ?? 'mx-6 mb-3 mt-1'}>
      <HeadlineStats stats={headlineStats} />
    </div>
  );
});

InventoryMetricsBar.displayName = 'InventoryMetricsBar';
